import { Component, inject, OnInit, PLATFORM_ID } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { GlobalMessageService, GlobalMessageType, TranslationService } from '@spartacus/core';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { ContactUsFormService } from 'src/app/services/feature/contact-us-form.service';
import { RegisterService } from 'src/app/services/feature/register.service';
import {
  ContactUsFormAPI,
  ContactUsFormPayload,
} from './contact-us-form.model';
import { PrivacyPolicyComponent } from './privacy-policy/privacy-policy.component';
import { isPlatformBrowser } from '@angular/common';

@Component({
    selector: 'kb-contact-us-form',
    templateUrl: './contact-us-form.component.html',
    styleUrls: ['./contact-us-form.component.scss'],
    standalone: false
})
export class KbContactUsFormComponent {
  submitted = false;
  countriesWSList: any;
  stateList$: Observable<any>;
  captchaResolved: boolean = false;
  isWorking = false;
  pageFieldData$ = this.registerService.getRegisterPageFields();
  topicList$: Observable<ContactUsFormAPI> =
    this.contactUsFormService.getContactUsFormTopics();
  platformId = inject(PLATFORM_ID);
  constructor(
    private dialog: MatDialog,
    private globalMessageService: GlobalMessageService,
    private registerService: RegisterService,
    private contactUsFormService: ContactUsFormService,
    private translationService: TranslationService
  ) { }

  contactUsForm = new FormGroup({
    topic: new FormControl(null, [Validators.required]),
    firstName: new FormControl(null, [Validators.required]),
    lastName: new FormControl(null, [Validators.required]),
    companyName: new FormControl(null),
    streetName: new FormControl(null, [Validators.required]),
    city: new FormControl(null, [Validators.required]),
    zip: new FormControl(null, [Validators.required]),
    phone: new FormControl(null),
    email: new FormControl(null, [Validators.email, Validators.required]),
    country: new FormControl(null, [Validators.required]),
    state: new FormControl(null, [Validators.required]),
    yourMessage: new FormControl(null, [Validators.required]),
    generalCondition: new FormControl(false, [Validators.required]),
    recaptcha: new FormControl(null, [Validators.required]),
  });

  // convenience getter for easy access to form controls
  get f() {
    return this.contactUsForm.controls;
  }

  checkCaptcha(captchaResponse: string) {
    this.captchaResolved =
      captchaResponse && captchaResponse.length > 0 ? true : false;
  }

  onSubmit() {
    this.submitted = true;
    this.isWorking = true;
    if (
      this.contactUsForm.invalid &&
      !this.captchaResolved &&
      this.contactUsForm.controls['generalCondition'].value !== true
    ) {
      this.globalMessageService.add({ key: 'common.text_contactusform_submission_error' }, GlobalMessageType.MSG_TYPE_ERROR);
      return;
    }
    if (this.contactUsForm.valid && this.captchaResolved) {
      const payload: ContactUsFormPayload = {
        topic: this.contactUsForm.get('topic').value,
        firstName: this.contactUsForm.get('firstName').value,
        lastName: this.contactUsForm.get('lastName').value,
        companyName: this.contactUsForm.get('companyName').value || '',
        companyStreet: this.contactUsForm.get('streetName').value,
        companyCity: this.contactUsForm.get('city').value,
        companyZip: this.contactUsForm.get('zip').value,
        companyCountryCode: this.contactUsForm.get('country').value,
        companyStateCode: this.contactUsForm.get('state').value,
        companyPhoneNumber: this.contactUsForm.get('phone').value,
        companyFaxNumber: this.contactUsForm.get('phone').value,
        email: this.contactUsForm.get('email').value,
        contactusComments: this.contactUsForm.get('yourMessage').value,
        termsOfUsageAcceptance:
          this.contactUsForm.get('generalCondition').value.toString(),
        captchaCode: !!this.contactUsForm.get('recaptcha').value,
      };
      this.contactUsFormService
        .submitContactUsData(payload)
        .pipe(take(1))
        .subscribe((res: any) => {
          this.isWorking = false
          if (res?.status === 'ERROR') {
            this.globalMessageService.add(
              { key: res?.message },
              GlobalMessageType.MSG_TYPE_ERROR
            );
          } else {
            this.globalMessageService.add(
              { key: res?.message },
              GlobalMessageType.MSG_TYPE_CONFIRMATION
            );
            if (isPlatformBrowser(this.platformId)) {
            document.documentElement.scrollTop = 0;
            }
            this.submitted = false
            this.contactUsForm.reset();
          }
        }, () => this.isWorking = false);
    }
  }

  onCountryChange(ev) {
    this.stateList$ = this.registerService.getRegions(ev.target.value);
  }

  openGeneralConditionDialog() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    const dialogRef = this.dialog.open(
      PrivacyPolicyComponent,
      dialogConfig
    );
    dialogRef.afterClosed().subscribe((data) => {
      if (data) {
        this.contactUsForm.controls['generalCondition'].patchValue(data);
      }
    });
  }

  generalConditionClick(ev) {
    this.contactUsForm.controls['generalCondition'].patchValue(
      ev.target.checked
    );
  }
}
